import * as THREE from "three";
import { BRICK_COLORS } from "../bricks/palette";
import { maxDpr, quality } from "../quality";

export type SceneBundle = {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  applyQuality: () => void;
  dispose: () => void;
};

export function createScene(canvas: HTMLCanvasElement): SceneBundle {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x140f18);
  scene.fog = new THREE.FogExp2(0x1a1420, 0.024);

  const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 120);
  camera.position.set(0, 10, 16);
  camera.lookAt(0, 2, 0);

  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: false,
    powerPreference: "high-performance",
  });
  renderer.setPixelRatio(maxDpr());
  renderer.shadowMap.enabled = !quality.low;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  // Lights — cozy studio, still cyan/red accents
  const hemi = new THREE.HemisphereLight(0xffe4d4, 0x2a2038, 0.78);
  scene.add(hemi);

  const key = new THREE.DirectionalLight(0xfff4e6, 0.72);
  key.position.set(8, 18, 10);
  key.castShadow = !quality.low;
  key.shadow.mapSize.set(quality.low ? 512 : 2048, quality.low ? 512 : 2048);
  key.shadow.camera.near = 1;
  key.shadow.camera.far = 50;
  key.shadow.camera.left = -20;
  key.shadow.camera.right = 20;
  key.shadow.camera.top = 20;
  key.shadow.camera.bottom = -20;
  scene.add(key);

  const cyan = new THREE.PointLight(BRICK_COLORS.cyan, 22, 36, 2);
  cyan.position.set(0, 4, 0);
  scene.add(cyan);

  const red = new THREE.PointLight(BRICK_COLORS.brightRed, 10, 28, 2);
  red.position.set(-6, 5, -4);
  scene.add(red);

  const amber = new THREE.PointLight(BRICK_COLORS.orange, 10, 26, 2);
  amber.position.set(6, 5, -4);
  scene.add(amber);

  // subtle star field
  const stars = makeStars(quality.low ? 120 : 500);
  scene.add(stars);

  const onResize = () => {
    const vv = window.visualViewport;
    const w = vv?.width ?? canvas.clientWidth ?? window.innerWidth;
    const h = vv?.height ?? canvas.clientHeight ?? window.innerHeight;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h, false);
  };
  window.addEventListener("resize", onResize);
  window.visualViewport?.addEventListener("resize", onResize);
  onResize();

  const applyQuality = () => {
    renderer.setPixelRatio(maxDpr());
    renderer.shadowMap.enabled = !quality.low;
    key.castShadow = !quality.low;
    onResize();
  };

  return {
    scene,
    camera,
    renderer,
    applyQuality,
    dispose: () => {
      window.removeEventListener("resize", onResize);
      window.visualViewport?.removeEventListener("resize", onResize);
      renderer.dispose();
    },
  };
}

function makeStars(count: number): THREE.Points {
  const positions = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const r = 40 + Math.random() * 40;
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = r * Math.cos(phi) * 0.6 + 10;
    positions[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  const mat = new THREE.PointsMaterial({
    color: 0x88ccff,
    size: 0.08,
    transparent: true,
    opacity: 0.7,
    sizeAttenuation: true,
  });
  return new THREE.Points(geo, mat);
}
